import discoverPlaceApi from 'api/discoverPlace';
import FormField from 'components/AddLocation/FormField';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import Button from 'components/App/Button';
import IconStarDiscovery from 'components/App/IconStarDiscovery';
import InputValidation from 'components/App/InputValidation';
import Pagination from 'components/Pagination';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Label, ModalBody, ModalHeader, Spinner, Table } from 'reactstrap';
import { apiErrorHandler, getTotalReviewCount, showSuccess } from 'utils';

const LIMIT = 10;

function LoadingProvider({ children }) {
  const [loading, setLoading] = useState(false);
  return children({ loading, setLoading });
}

function ModalAddPlace({ provinceId, discoverCategories, onClose, onRefreshData }) {
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState({
    data: [],
    total: 0,
  });

  const totalPage = useMemo(() => Math.ceil(listData.total / LIMIT),
    [listData.total]);

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await discoverPlaceApi.getDiscoverPlaceList(params);

      setListData({
        data: res.payload.data,
        total: res.payload.total,
      });
    } catch (error) {
      apiErrorHandler(error, true);
    } finally {
      setLoading(false);
    }
  }, []);

  const validation = useFormik({
    initialValues: {
      q: '',
      page: 1,
    },
    onSubmit: (values) => {
      fetchData({
        q: values.q,
        limit: LIMIT,
        page: 1,
        discoverCategories: discoverCategories
          .map((category) => category.value),
        hasDiscoverProvince: false,
      });
    },
  });

  const columns = useMemo(() => [
    {
      header: 'Tên điểm khám phá',
      render: (item) => item.name,
    },
    {
      header: 'Điểm đánh giá',
      render: (item) => (
        <IconStarDiscovery
          star={item.starRating ?? 0}
          size={12}
        />
      ),
    },
    {
      header: 'Tổng lượt đánh giá',
      render: (item) => getTotalReviewCount(item),
    },
    {
      header: 'Thao tác',
      render: (item) => (
        <LoadingProvider>
          {({ loading: loadingButton, setLoading: setLoadingButton }) => (
            <div>
              <Button
                type="button"
                color="secondary"
                onClick={async () => {
                  setLoadingButton(true);
                  try {
                    await discoverPlaceApi
                      .updateDiscoverPlaceDiscoverProvince(item._id, {
                        discoverProvince: provinceId,
                      });

                    showSuccess('Thêm điểm khám phá thành công');

                    setListData((prev) => {
                      const newData = prev
                        .data
                        .filter((i) => i._id !== item._id);
                      const oldLength = prev.data.length;
                      const newLength = newData.length;
                      return ({
                        ...prev,
                        data: newData,
                        total: oldLength !== newLength ? prev.total - 1 : prev.total,
                      });
                    });

                    onRefreshData();
                  } catch (error) {
                    apiErrorHandler(error, true);
                  } finally {
                    setLoadingButton(false);
                  }
                }}
                isDisabled={loadingButton}
              >
                {loadingButton ? <Spinner size="sm" /> : 'Thêm'}
              </Button>
            </div>
          )}
        </LoadingProvider>

      ),
    },
  ], []);

  useEffect(() => {
    fetchData({
      limit: LIMIT,
      page: validation.values.page,
      discoverCategories: discoverCategories
        .map((category) => category.value),
      hasDiscoverProvince: false,
    });
  }, [validation.values.page]);

  return (
    <>
      <ModalHeader
        close={(
          <AppCloseModalButton
            onClick={onClose}
            className="custom_button_close"
          />
        )}
      >
        Thêm điểm khám phá
      </ModalHeader>

      <ModalBody>
        <div className="mb-24">
          <FormField>
            <Label>Tìm kiếm</Label>

            <div className="d-flex gap-4">
              <div className="flex-grow-1">
                <InputValidation
                  name="q"
                  placeholder="Nhập tên điểm khám phá"
                  validation={validation}
                />
              </div>

              <Button type="button" onClick={() => validation.handleSubmit()}>
                Tìm kiếm
              </Button>
            </div>
          </FormField>
        </div>

        <div className="position-relative">
          {loading && (
            <div
              className="position-absolute"
              style={{
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                zIndex: 10,
              }}
            >
              <Spinner />
            </div>
          )}

          <Table responsive className="custom_table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.header}>{column.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listData.data.map((item) => (
                <tr key={item._id}>
                  {columns.map((column) => (
                    <td key={column.header}>{column.render(item)}</td>
                  ))}
                </tr>
              ))}

              {listData.data.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    Không có dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <Pagination
            page={validation.values.page}
            pageCount={totalPage}
            onChangePage={({ selected }) => {
              validation.setFieldValue('page', selected + 1);
            }}
          />
        </div>
      </ModalBody>
    </>
  );
}

ModalAddPlace.propTypes = {
  provinceId: PropTypes.string.isRequired,
  discoverCategories: PropTypes.instanceOf(Array).isRequired,
  onClose: PropTypes.func.isRequired,
  onRefreshData: PropTypes.func.isRequired,
};

export default ModalAddPlace;
